import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Inventory2 as PackagingIcon,
  Refresh as RefreshIcon,
  Rule as RuleIcon
} from '@mui/icons-material';
import { useBrand } from '../../context/BrandContext';
import { useInventoryBrand } from '../../hooks/useInventoryBrand';
import { excludeCompanyLevel } from '../../utils/brandFilters';
import {
  createPackagingRule,
  createPackagingSku,
  deletePackagingRule,
  listPackagingChannels,
  listPackagingProducts,
  listPackagingRules,
  listPackagingSkus,
  listPackagingWarehouses,
  recalculatePackagingConsumption,
  updatePackagingRule,
  updatePackagingSku
} from '../../services/packagingService';

const emptySku = {
  primary_sku: '',
  product_name: '',
  lead_time_days: 0,
  moq: 1,
  case_pack: 1,
  notes: '',
  is_active: true
};

const emptyRule = {
  channel: 'ALL',
  warehouse_code: 'ALL',
  trigger_type: 'per_order',
  match_sku: '',
  packaging_sku: '',
  quantity: 1,
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: '2999-12-31',
  notes: ''
};

const numberFmt = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 });

function formatNumber(value) {
  return numberFmt.format(Number(value || 0));
}

function statusColor(status) {
  if (status === 'critical') return 'error';
  if (status === 'warning') return 'warning';
  if (status === 'ok') return 'success';
  return 'default';
}

function normalizeError(error, fallback) {
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
}

function PackagingConsumables() {
  const { selectedBrand, brands, selectBrand } = useInventoryBrand();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [search, setSearch] = useState('');
  const [skus, setSkus] = useState([]);
  const [rules, setRules] = useState([]);
  const [channels, setChannels] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [skuDialog, setSkuDialog] = useState({ open: false, data: null });
  const [ruleDialog, setRuleDialog] = useState({ open: false, data: null });

  const brandId = selectedBrand?.id || '';

  const summary = useMemo(() => {
    return skus.reduce((acc, sku) => {
      acc.totalStock += Number(sku.total_stock || 0);
      acc.consumed30 += Number(sku.consumed_30d || 0);
      if (sku.reorder_status === 'critical' || sku.reorder_status === 'warning') acc.needsAction += 1;
      return acc;
    }, { totalStock: 0, consumed30: 0, needsAction: 0 });
  }, [skus]);

  useEffect(() => {
    if (brandId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const loadData = async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { brand_id: brandId, include_inactive: 'true', limit: 1000 };
      if (search.trim()) params.search = search.trim();
      const [skuRes, ruleRes, channelRes, warehouseRes] = await Promise.all([
        listPackagingSkus(params),
        listPackagingRules({ brand_id: brandId }),
        listPackagingChannels(),
        listPackagingWarehouses()
      ]);
      setSkus(skuRes.skus || []);
      setRules(ruleRes.rules || []);
      setChannels(channelRes.channels || []);
      setWarehouses(warehouseRes.warehouses || []);
    } catch (err) {
      setError(normalizeError(err, 'Failed to load packaging consumables'));
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async (term = '') => {
    if (!brandId) return;
    try {
      const res = await listPackagingProducts({ brand_id: brandId, search: term });
      setProducts(res.products || []);
    } catch (err) {
      setError(normalizeError(err, 'Failed to load product SKUs'));
    }
  };

  const openSkuDialog = (sku = null) => {
    setError(null); setSkuDialog({
      open: true,
      data: sku ? { ...sku, is_active: Boolean(sku.is_active) } : { ...emptySku, brand_id: brandId }
    });
  };

  const openRuleDialog = async (rule = null) => {
    // DT-1922: clear before the load, so a 'Failed to load product SKUs' from loadProducts stays and shows in the dialog.
    setError(null);
    await loadProducts('');
    setRuleDialog({
      open: true,
      data: rule ? {
        ...rule,
        effective_from: String(rule.effective_from || '').slice(0, 10),
        effective_to: String(rule.effective_to || '').slice(0, 10)
      } : { ...emptyRule, brand_id: brandId }
    });
  };

  const saveSku = async () => {
    const data = skuDialog.data;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...data,
        brand_id: brandId,
        lead_time_days: Number(data.lead_time_days || 0),
        moq: Number(data.moq || 1),
        case_pack: Number(data.case_pack || 1)
      };
      if (data.id) {
        await updatePackagingSku(data.id, payload);
        setSuccess('Packaging SKU updated');
      } else {
        await createPackagingSku(payload);
        setSuccess('Packaging SKU created');
      }
      setSkuDialog({ open: false, data: null });
      await loadData();
    } catch (err) {
      setError(normalizeError(err, 'Failed to save packaging SKU'));
    } finally {
      setSaving(false);
    }
  };

  const saveRule = async () => {
    const data = ruleDialog.data;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...data,
        brand_id: brandId,
        match_sku: data.trigger_type === 'per_product' ? data.match_sku : '',
        quantity: Number(data.quantity || 0)
      };
      if (data.id) {
        await updatePackagingRule(data.id, payload);
        setSuccess('Packaging rule updated');
      } else {
        await createPackagingRule(payload);
        setSuccess('Packaging rule created');
      }
      setRuleDialog({ open: false, data: null });
      await loadData();
    } catch (err) {
      setError(normalizeError(err, 'Failed to save packaging rule'));
    } finally {
      setSaving(false);
    }
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`Deactivate rule for ${rule.packaging_sku}?`)) return;
    setError(null);
    try {
      await deletePackagingRule(rule.id, { effective_date: new Date().toISOString().slice(0, 10) });
      setSuccess('Packaging rule deactivated');
      await loadData();
    } catch (err) {
      setError(normalizeError(err, 'Failed to deactivate rule'));
    }
  };

  const recalculate = async () => {
    if (!brandId) return;
    setSaving(true);
    setError(null);
    try {
      const dateTo = new Date().toISOString().slice(0, 10);
      const d = new Date(`${dateTo}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() - 30);
      await recalculatePackagingConsumption({
        brand_id: brandId,
        date_from: d.toISOString().slice(0, 10),
        date_to: dateTo
      });
      setSuccess('Packaging recalculation queued');
    } catch (err) {
      setError(normalizeError(err, 'Failed to queue recalculation'));
    } finally {
      setSaving(false);
    }
  };

  const updateSkuForm = (patch) => setSkuDialog(prev => ({ ...prev, data: { ...prev.data, ...patch } }));
  const updateRuleForm = (patch) => setRuleDialog(prev => ({ ...prev, data: { ...prev.data, ...patch } }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PackagingIcon color="primary" sx={{ fontSize: 34 }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>Packaging Consumables</Typography>
            <Typography variant="body2" color="text.secondary">Packaging SKUs, stock, usage and shipment consumption rules</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              label="Brand"
              value={brandId}
              onChange={(e) => {
                const brand = brands.find(b => String(b.id) === String(e.target.value));
                if (brand) selectBrand(brand);
              }}
            >
              {excludeCompanyLevel(brands).map(brand => (
                <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} disabled={loading || !brandId}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={recalculate} disabled={saving || !brandId}>
            Recalculate
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {!brandId && <Alert severity="info">Select a brand to manage packaging consumables.</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography color="text.secondary" variant="body2">Packaging SKUs</Typography><Typography variant="h5">{formatNumber(skus.length)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography color="text.secondary" variant="body2">Current Stock</Typography><Typography variant="h5">{formatNumber(summary.totalStock)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography color="text.secondary" variant="body2">Used 30d</Typography><Typography variant="h5">{formatNumber(summary.consumed30)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography color="text.secondary" variant="body2">Needs Action</Typography><Typography variant="h5">{formatNumber(summary.needsAction)}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Packaging SKUs" icon={<PackagingIcon />} iconPosition="start" />
          <Tab label="Consumption Rules" icon={<RuleIcon />} iconPosition="start" />
        </Tabs>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadData(); }}
            sx={{ minWidth: 280 }}
          />
          {tab === 0 ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openSkuDialog()} disabled={!brandId}>Add SKU</Button>
          ) : (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openRuleDialog()} disabled={!brandId || skus.length === 0}>Add Rule</Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : tab === 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="right">Incoming</TableCell>
                <TableCell align="right">Used 30d</TableCell>
                <TableCell align="right">Days Cover</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Suggested PO</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {skus.map(sku => (
                <TableRow key={sku.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{sku.primary_sku}</TableCell>
                  <TableCell>{sku.product_name}</TableCell>
                  <TableCell align="right">{formatNumber(sku.total_stock)}</TableCell>
                  <TableCell align="right">{formatNumber(sku.incoming_stock)}</TableCell>
                  <TableCell align="right">{formatNumber(sku.consumed_30d)}</TableCell>
                  <TableCell align="right">{sku.days_of_cover == null ? '-' : formatNumber(sku.days_of_cover)}</TableCell>
                  <TableCell><Chip size="small" label={sku.reorder_status || 'unknown'} color={statusColor(sku.reorder_status)} /></TableCell>
                  <TableCell align="right">{formatNumber(sku.suggested_reorder_qty)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit SKU">
                      <IconButton size="small" onClick={() => openSkuDialog(sku)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {skus.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}>No packaging SKUs found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Packaging SKU</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Product SKU</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Effective</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(rule => (
                <TableRow key={`${rule.id}-${rule.effective_from}`} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{rule.packaging_sku}</TableCell>
                  <TableCell>{rule.trigger_type === 'per_product' ? 'Per product' : 'Per order'}</TableCell>
                  <TableCell>{rule.match_sku || '-'}</TableCell>
                  <TableCell>{rule.channel === 'ALL' ? 'All channels' : rule.channel}</TableCell>
                  <TableCell>{rule.warehouse_code === 'ALL' ? 'All warehouses' : rule.warehouse_code}</TableCell>
                  <TableCell align="right">{formatNumber(rule.quantity)}</TableCell>
                  <TableCell>{String(rule.effective_from).slice(0, 10)} to {String(rule.effective_to).slice(0, 10)}</TableCell>
                  <TableCell><Chip size="small" label={rule.is_current ? 'Current' : 'Inactive'} color={rule.is_current ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit rule">
                      <IconButton size="small" onClick={() => openRuleDialog(rule)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate rule">
                      <IconButton size="small" onClick={() => removeRule(rule)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rules.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}>No packaging rules found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={skuDialog.open} onClose={() => setSkuDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{skuDialog.data?.id ? 'Edit Packaging SKU' : 'Add Packaging SKU'}</DialogTitle>
        {skuDialog.data && (
          <DialogContent sx={{ pt: 2 }}>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth size="small" label="SKU" value={skuDialog.data.primary_sku || ''} disabled={Boolean(skuDialog.data.id)} onChange={(e) => updateSkuForm({ primary_sku: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField fullWidth size="small" label="Name" value={skuDialog.data.product_name || ''} onChange={(e) => updateSkuForm({ product_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="number" label="Lead Time Days" value={skuDialog.data.lead_time_days || 0} onChange={(e) => updateSkuForm({ lead_time_days: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="number" label="MOQ" value={skuDialog.data.moq || 1} onChange={(e) => updateSkuForm({ moq: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="number" label="Case Pack" value={skuDialog.data.case_pack || 1} onChange={(e) => updateSkuForm({ case_pack: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2} label="Notes" value={skuDialog.data.notes || ''} onChange={(e) => updateSkuForm({ notes: e.target.value })} />
              </Grid>
              {skuDialog.data.id && (
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch checked={Boolean(skuDialog.data.is_active)} onChange={(e) => updateSkuForm({ is_active: e.target.checked })} />} label="Active" />
                </Grid>
              )}
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setSkuDialog({ open: false, data: null })}>Cancel</Button>
          <Button variant="contained" onClick={saveSku} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ruleDialog.open} onClose={() => setRuleDialog({ open: false, data: null })} maxWidth="md" fullWidth>
        <DialogTitle>{ruleDialog.data?.id ? 'Edit Packaging Rule' : 'Add Packaging Rule'}</DialogTitle>
        {ruleDialog.data && (
          <DialogContent sx={{ pt: 2 }}>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Trigger</InputLabel>
                  <Select label="Trigger" value={ruleDialog.data.trigger_type} onChange={(e) => updateRuleForm({ trigger_type: e.target.value, match_sku: '' })}>
                    <MenuItem value="per_order">Per order</MenuItem>
                    <MenuItem value="per_product">Per product</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Packaging SKU</InputLabel>
                  <Select label="Packaging SKU" value={ruleDialog.data.packaging_sku || ''} onChange={(e) => updateRuleForm({ packaging_sku: e.target.value })}>
                    {skus.filter(s => s.is_active !== 0).map(sku => (
                      <MenuItem key={sku.primary_sku} value={sku.primary_sku}>{sku.primary_sku}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="number" label="Quantity" value={ruleDialog.data.quantity || 1} onChange={(e) => updateRuleForm({ quantity: e.target.value })} />
              </Grid>
              {ruleDialog.data.trigger_type === 'per_product' && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product SKU</InputLabel>
                    <Select label="Product SKU" value={ruleDialog.data.match_sku || ''} onChange={(e) => updateRuleForm({ match_sku: e.target.value })}>
                      {products.map(product => (
                        <MenuItem key={product.primary_sku} value={product.primary_sku}>{product.primary_sku} · {product.product_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Channel</InputLabel>
                  <Select label="Channel" value={ruleDialog.data.channel || 'ALL'} onChange={(e) => updateRuleForm({ channel: e.target.value })}>
                    <MenuItem value="ALL">All channels</MenuItem>
                    {channels.map(channel => (
                      <MenuItem key={channel.value} value={channel.value}>{channel.label || channel.value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Warehouse</InputLabel>
                  <Select label="Warehouse" value={ruleDialog.data.warehouse_code || 'ALL'} onChange={(e) => updateRuleForm({ warehouse_code: e.target.value })}>
                    <MenuItem value="ALL">All warehouses</MenuItem>
                    {warehouses.map(warehouse => (
                      <MenuItem key={warehouse.code} value={warehouse.code}>{warehouse.name || warehouse.code}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="date" label="Effective From" InputLabelProps={{ shrink: true }} value={ruleDialog.data.effective_from || ''} onChange={(e) => updateRuleForm({ effective_from: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="date" label="Effective To" InputLabelProps={{ shrink: true }} value={ruleDialog.data.effective_to || ''} onChange={(e) => updateRuleForm({ effective_to: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2} label="Notes" value={ruleDialog.data.notes || ''} onChange={(e) => updateRuleForm({ notes: e.target.value })} />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setRuleDialog({ open: false, data: null })}>Cancel</Button>
          <Button variant="contained" onClick={saveRule} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PackagingConsumables;
